import { useSortable } from "@dnd-kit/sortable";
import React, { useEffect, useState } from "react";
import { CSS } from "@dnd-kit/utilities";
import { SubOptionType } from "../../../services/helpers/types";
import Button from "../Button/Button";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { DeleteOutline } from "@mui/icons-material";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import TextField from "../TextField/TextField";
import { validateOptionValue } from "../../../services/helpers/commonHelpers";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

interface DraggableSelectSubOptionsItemProps {
  id: number;
  option: SubOptionType | undefined;
  subOptions: SubOptionType[];
  onOptionChange: (option: SubOptionType) => void;
  onDelete: (itemID: number) => void;
}

const DraggableSelectSubOptionsItem = (
  props: DraggableSelectSubOptionsItemProps
) => {
  const { id, option, subOptions, onDelete, onOptionChange } = props;

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const [optionName, setOptionName] = useState(option?.name ?? "");
  const [optionValue, setOptionValue] = useState(option?.value ?? "");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  useEffect(() => {
    setOptionName(option?.name ?? "");
    setOptionValue(option?.value ?? "");
  }, [option]);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleConfirmDelete = () => {
    if (option?.position !== undefined) {
      onDelete(option.position);
      setIsDeleteOpen(false);
    }
  };

  const handleOptionNameChange = (optionName: string) => {
    setOptionName(optionName);
    if (option) {
      const newOption: SubOptionType = {
        ...option,
        name: optionName,
      };
      onOptionChange(newOption);
    }
  };

  const handleOptionValueChange = (optionValue: string) => {
    if (option) {
      const newOption: SubOptionType = {
        ...option,
        value: optionValue,
      };

      if (!validateOptionValue(newOption, subOptions, optionValue, dispatch)) {
        return;
      }
      setOptionValue(optionValue);
      onOptionChange(newOption);
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="dui-suboptions-edit-item">
      <div className="dui-suboptions-edit-item-buttons">
        <div {...attributes} {...listeners} className="dui-btn-inner">
          <Button icon={<UnfoldMoreIcon />} type="text" />
        </div>
        <div className="dui-btn-inner">
          <Button
            icon={<DeleteOutline />}
            type="text"
            onClick={() => {
              setIsDeleteOpen(true);
            }}
          />
        </div>
      </div>
      <div className="dui-suboptions-edit-item-name">
        <div className="dui-suboptions-edit-item-name-field">
          <div>{t("subOptionName")}:</div>
          <TextField value={optionName} onChange={handleOptionNameChange} />
        </div>
        <div className="dui-suboptions-edit-item-name-dash">-</div>
        <div className="dui-suboptions-edit-item-name-field">
          <div>{t("reportValue")}:</div>
          <TextField value={optionValue} onChange={handleOptionValueChange} />
        </div>
      </div>
      <ConfirmDialog
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t("deleteTitle")}
        description={`${t("areYouSureDelete1")} ${option?.name}${t(
          "areYouSureDelete2"
        )}`}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        confirmButtonAppearance="error"
      />
    </div>
  );
};

export default DraggableSelectSubOptionsItem;
